import './underwriting.overview.policy-holder-info.scss';

import {
  ConvergeFormTable,
  CountryFormSearchableSelectField,
  FormContainerWithHeader,
  FormNumberField,
  FormNumberInputAdornmentField,
  IFormNumberInputAdornmentFieldProps,
  IndustrySectorFormSearchableSelectField,
  NewFormTextField,
  RegionFormSearchableSelectField,
} from '../../../../presentation';
import {
  SubmissionDetailInsuredEntityAddressData,
  SubmissionDetailInsuredEntityAliasData,
  SubmissionDetailInsuredEntityDomainData,
  SubmissionDetailInsuredEntityFundamentalsData,
  SubmissionDetailInsuredEntityIndustrySectorData,
  providerListWithoutPrimaryToDisplay,
  providerToDisplay,
} from '@converge/graphql';
import {
  capitalizeFirstLetter,
  checkPositiveNumber,
  convertInputToFormattedAmountUSD,
} from '@converge/utils';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { Alert, Box, Grid } from '@mui/material';
import { UnderwritingOverviewUnderwritingFinancialAnalysisSummary } from './underwriting.overview.underwriting-financial-analysis-summary';
import {
  CreateSubmissionField,
  NAICS_PLACEHOLDER_CODE,
  NAICS_PLACEHOLDER_MESSAGE,
  getCreateSubmissionFieldLabel,
  getFieldProps,
} from '@converge/data';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnderwritingSubmissionStateContext } from '../../underwriting.submission.context';

interface IProps {
  isReadonly?: boolean;
  upsertSubmissionIsLoading?: boolean;
}

const NullSafeFormNumberInputAdornmentField = (
  props: IFormNumberInputAdornmentFieldProps
) => {
  const { watch } = useFormContext();
  const fieldValue = watch(props.name);
  const numericValue =
    fieldValue != null && fieldValue !== '' && !isNaN(Number(fieldValue))
      ? Number(fieldValue)
      : null;
  return <FormNumberInputAdornmentField {...props} value={numericValue} />;
};

export const UnderwritingOverviewPolicyHolderInfoFormSection = ({
  isReadonly,
  upsertSubmissionIsLoading,
}: IProps) => {
  const { t } = useTranslation();

  const { control, setValue } = useFormContext();

  const {
    submission: { insuredEntity, hasPlaceholderNaics },
  } = useUnderwritingSubmissionStateContext();

  const storedCountryIso2 = useWatch({
    control,
    name: 'insuredEntity.primaryAddress.iso2Code',
  });

  const [selectedCountry, setSelectedCountry] = useState(
    storedCountryIso2 || 'US'
  );

  useEffect(() => {
    setSelectedCountry(storedCountryIso2 || 'US');
  }, [storedCountryIso2]);
  const [RegionName, setRegionName] = useState<string>('State');
  const { fields: tradingNamesField } = useFieldArray({
    control,
    name: 'insuredEntity.tradingNames',
  });

  const { fields: domainsField } = useFieldArray({
    control,
    name: 'insuredEntity.domains',
  });

  const { fields: otherAddressesField } = useFieldArray({
    control,
    name: 'insuredEntity.otherAddresses',
  });

  const { fields: otherIndustrySectorsField } = useFieldArray({
    control,
    name: 'insuredEntity.otherIndustrySectors',
  });
  const otherIndustrySectorsRows = (
    otherIndustrySectorsField as unknown as SubmissionDetailInsuredEntityIndustrySectorData[]
  ).filter((row) => row.naicsCode !== NAICS_PLACEHOLDER_CODE);

  const { fields: otherLegalAliases } = useFieldArray({
    control,
    name: 'insuredEntity.otherLegalAliases',
  });

  const { fields: otherFundamentals } = useFieldArray({
    control,
    name: 'insuredEntity.otherFundamentals',
  });

  return (
    <FormContainerWithHeader title={t('Insured')}>
      {hasPlaceholderNaics && (
        <Grid item xs={12}>
          <Alert severity="error">{t(NAICS_PLACEHOLDER_MESSAGE)}</Alert>
        </Grid>
      )}
      <NewFormTextField
        {...getFieldProps(
          'insuredEntity.primaryLegalAlias.name',
          isReadonly,
          t('LEGAL NAME')
        )}
        required
        placeholder="Enter Legal Name"
      />
      <NewFormTextField
        label="ENTITY REFERENCE ID"
        name="insuredEntity.referenceId"
        value={insuredEntity?.node.referenceId}
        disabled
      />
      <ConvergeFormTable<SubmissionDetailInsuredEntityAliasData>
        title={t('Identified Legal Names')}
        rows={
          otherLegalAliases as unknown as SubmissionDetailInsuredEntityAliasData[]
        }
        baseFieldPath="insuredEntity.otherLegalAliases"
        columns={[
          {
            id: 'name',
            label: 'Legal name',
          },
          {
            width: '20%',
            id: 'source',
            label: 'Data source',
            render: (row) =>
              providerListWithoutPrimaryToDisplay(row.sources).join(', '),
          },
        ]}
      />
      <NewFormTextField
        {...getFieldProps(
          'insuredEntity.primaryTradingName.name',
          isReadonly,
          t('DBA')
        )}
        placeholder="Enter DBA"
      />
      <ConvergeFormTable<SubmissionDetailInsuredEntityAliasData>
        title={t('Identified DBAs')}
        rows={
          tradingNamesField as unknown as SubmissionDetailInsuredEntityAliasData[]
        }
        baseFieldPath="insuredEntity.tradingNames"
        columns={[
          {
            id: 'name',
            label: 'DBA',
          },
          {
            width: '20%',
            id: 'source',
            label: 'Data source',
            render: (row) =>
              providerListWithoutPrimaryToDisplay(row.sources).join(', '),
          },
        ]}
      />
      <NewFormTextField
        {...getFieldProps('insuredEntity.primaryDomain', true, t('DOMAIN'))}
      />

      <ConvergeFormTable
        title={t('Identified Domains')}
        rows={
          domainsField as unknown as SubmissionDetailInsuredEntityDomainData[]
        }
        baseFieldPath="insuredEntity.domains"
        enablePagination
        columns={[
          {
            id: 'domain',
            label: 'Domain',
          },
          {
            width: '50%',
            id: 'source',
            label: 'Data source',
            render: (row) =>
              providerListWithoutPrimaryToDisplay(row.sources)?.join(', '),
          },
        ]}
      />
      <NewFormTextField
        {...getFieldProps(
          'insuredEntity.primaryAddress.street1',
          isReadonly,

          'ADDRESS 1'
        )}
        required
        placeholder="Enter Address 1"
      />
      <NewFormTextField
        {...getFieldProps(
          'insuredEntity.primaryAddress.street2',
          isReadonly,

          'ADDRESS 2'
        )}
        placeholder="Enter Address 2"
      />
      <NewFormTextField
        {...getFieldProps(
          'insuredEntity.primaryAddress.city',
          isReadonly,

          'CITY'
        )}
        required
        placeholder="Enter City"
      />
      <RegionFormSearchableSelectField
        minDropdownWidth="530px"
        maxDropdownWidth="750px"
        {...getFieldProps(
          'insuredEntity.primaryAddress.region',
          isReadonly,
          capitalizeFirstLetter(RegionName)
        )}
        selectedCountry={selectedCountry}
        setRegionName={setRegionName}
        required
        placeholder="Select State"
        onChangeOption={(listOption) => {
          if (listOption) {
            setValue('insuredEntity.primaryAddress.region', listOption?.value, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        }}
      />
      <NewFormTextField
        {...getFieldProps(
          'insuredEntity.primaryAddress.postalCode',
          isReadonly,

          'POSTAL CODE'
        )}
        required
        placeholder="Enter Postal Code"
      />
      {/* <CountryFormSelectField
        {...getFieldProps(
          'insuredEntity.primaryAddress.country',
          isReadonly,
          t(
            `underwriting.detail.content.sections.policyHolderInformation.fields.country`,
            'Country'
          )
        )}
      /> */}
      <CountryFormSearchableSelectField
        minDropdownWidth="530px"
        maxDropdownWidth="750px"
        {...getFieldProps(
          'insuredEntity.primaryAddress.country',
          isReadonly,

          'COUNTRY'
        )}
        placeholder="Select Country"
        autoComplete={false}
        required
        onChangeOption={(listOption) => {
          if (listOption) {
            setSelectedCountry(listOption.data.iso2Code ?? '');
            setValue(
              'insuredEntity.primaryAddress.country',
              listOption?.value,
              {
                shouldDirty: true,
                shouldValidate: true,
              }
            );
          }
        }}
      />
      <ConvergeFormTable<SubmissionDetailInsuredEntityAddressData>
        title={t('Identified Addresses')}
        rows={
          otherAddressesField as unknown as SubmissionDetailInsuredEntityAddressData[]
        }
        baseFieldPath={`insuredEntity.otherAddresses`}
        columns={[
          {
            id: 'street1',
            label: 'Address 1',
          },
          {
            id: 'street2',
            label: 'Address 2',
          },
          {
            id: 'city',
            label: 'City',
          },
          {
            id: 'regionName',
            label: 'Region',
          },
          {
            id: 'postalCode',
            label: 'Postal Code',
          },
          {
            id: 'countryName',
            label: 'Country',
          },
          {
            id: 'source',
            label: 'Data Source',
            render: (row) => providerToDisplay(row.source),
          },
        ]}
      />
      <NewFormTextField
        {...getFieldProps(
          'insuredEntity.companyType',
          true,

          'COMPANY TYPE'
        )}
      />
      <NewFormTextField
        {...getFieldProps(
          'insuredEntity.primaryFundamentals.yearEstablished',
          true,

          'YEAR ESTABLISHED'
        )}
      />

      <IndustrySectorFormSearchableSelectField
        minDropdownWidth="530px"
        maxDropdownWidth="750px"
        {...getFieldProps(
          'insuredEntity.primaryIndustrySector.naicsCode',
          isReadonly,

          'INDUSTRY SECTOR (NAICS)'
        )}
        disableClearable={true}
        disabled={upsertSubmissionIsLoading}
        placeholder="Select Industry Sector"
        onChangeOption={(listOption) => {
          if (listOption) {
            setValue(
              'insuredEntity.primaryIndustrySector.naicsCode',
              listOption?.value,
              {
                shouldDirty: true,
                shouldValidate: true,
              }
            );
          }
        }}
      />
      <ConvergeFormTable<SubmissionDetailInsuredEntityIndustrySectorData>
        title={t('Identified Industry Sectors')}
        rows={otherIndustrySectorsRows}
        baseFieldPath="insuredEntity.otherIndustrySectors"
        columns={[
          {
            id: 'name',
            label: 'Industry sector',
          },
          {
            id: 'naicsCode',
            label: 'NAICS',
          },
          {
            id: 'source',
            label: 'Data source',
            render: (row) =>
              providerListWithoutPrimaryToDisplay(row.sources)?.join(', '),
          },
        ]}
      />
      <Box style={{ width: '100%', marginLeft: '66px' }}>
        <Box sx={{ display: 'flex' }} className="revenue-row">
          <NullSafeFormNumberInputAdornmentField
            adornmentText={'$'}
            {...getFieldProps(
              'insuredEntity.primaryFundamentals.ratedRevenueOverride',
              isReadonly,
              getCreateSubmissionFieldLabel(
                CreateSubmissionField.RatedRevenueOverride
              )
            )}
            isAllowed={(values) => checkPositiveNumber(values)}
            thousandSeparator=","
            required
            placeholder="0"
          />
          <Box
            className="estimated-revenue"
            sx={{
              '@media (min-width:1784px)': {
                maxWidth: '50%',
              },
            }}
          >
            <EstimatedRatableRevenueAlert />
          </Box>
        </Box>

        <Box className="revenue-row">
          <NullSafeFormNumberInputAdornmentField
            adornmentText={'$'}
            {...getFieldProps(
              'insuredEntity.primaryFundamentals.revenue',
              isReadonly,
              getCreateSubmissionFieldLabel(
                CreateSubmissionField.CurrentDomesticRevenue
              )
            )}
            thousandSeparator=","
            required
            isAllowed={(values) => checkPositiveNumber(values)}
            placeholder="0"
          />
          <NullSafeFormNumberInputAdornmentField
            adornmentText={'$'}
            {...getFieldProps(
              'insuredEntity.primaryFundamentals.projectedRevenue',
              isReadonly,
              getCreateSubmissionFieldLabel(
                CreateSubmissionField.ProjectDomesticRevenue
              )
            )}
            isAllowed={(values) => checkPositiveNumber(values)}
            thousandSeparator=","
            placeholder="0"
          />
        </Box>

        <Box className="revenue-row">
          <NullSafeFormNumberInputAdornmentField
            adornmentText={'$'}
            {...getFieldProps(
              'insuredEntity.primaryFundamentals.foreignRevenue',
              false,
              getCreateSubmissionFieldLabel(
                CreateSubmissionField.CurrentForeignRevenue
              )
            )}
            isAllowed={(values) => checkPositiveNumber(values)}
            thousandSeparator=","
            placeholder="  Enter Revenue"
          />
          <NullSafeFormNumberInputAdornmentField
            adornmentText={'$'}
            {...getFieldProps(
              'insuredEntity.primaryFundamentals.projectedForeignRevenue',
              false,
              getCreateSubmissionFieldLabel(
                CreateSubmissionField.ProjectForeignRevenue
              )
            )}
            isAllowed={(values) => checkPositiveNumber(values)}
            thousandSeparator=","
            placeholder=" Enter Revenue"
          />
        </Box>

        <Box className="revenue-row">
          <FormNumberField
            {...getFieldProps(
              'insuredEntity.primaryFundamentals.employees',
              isReadonly,
              'EMPLOYEES'
            )}
            thousandSeparator=","
            isAllowed={(values) => checkPositiveNumber(values)}
            placeholder="Enter Employees"
          />
          <NullSafeFormNumberInputAdornmentField
            adornmentText={'%'}
            {...getFieldProps(
              'insuredEntity.primaryFundamentals.currentRatio',
              false,
              'CURRENT RATIO'
            )}
            placeholder=" Enter Ratio"
            decimalScale={2}
            isAllowed={(values) => {
              const { formattedValue, floatValue } = values;
              if (floatValue === null || floatValue === undefined) {
                return formattedValue === '';
              } else {
                return floatValue >= 0 && floatValue <= 100;
              }
            }}
          />
          <NullSafeFormNumberInputAdornmentField
            adornmentText={'%'}
            {...getFieldProps(
              'insuredEntity.primaryFundamentals.quickRatio',
              false,
              'QUICK RATIO'
            )}
            placeholder=" Enter Ratio"
            decimalScale={2}
            isAllowed={(values) => {
              const { formattedValue, floatValue } = values;
              if (floatValue === null || floatValue === undefined) {
                return formattedValue === '';
              } else {
                return floatValue >= 0 && floatValue <= 100;
              }
            }}
          />
          <NullSafeFormNumberInputAdornmentField
            adornmentText={'%'}
            {...getFieldProps(
              'insuredEntity.primaryFundamentals.cashRatio',
              false,
              'CASH RATIO'
            )}
            placeholder=" Enter Ratio"
            decimalScale={2}
            isAllowed={(values) => {
              const { formattedValue, floatValue } = values;
              if (floatValue === null || floatValue === undefined) {
                return formattedValue === '';
              } else {
                return floatValue >= 0 && floatValue <= 100;
              }
            }}
          />
        </Box>
      </Box>

      <ConvergeFormTable<SubmissionDetailInsuredEntityFundamentalsData>
        title={t('Identified Firmographics')}
        rows={
          otherFundamentals as unknown as SubmissionDetailInsuredEntityFundamentalsData[]
        }
        baseFieldPath="insuredEntity.otherFundamentals"
        columns={[
          {
            id: `revenue`,
            label: 'DOMESTIC REVENUE',
            renderValue: (value) => convertInputToFormattedAmountUSD(value),
          },
          {
            id: 'foreignRevenue',
            label: 'FOREIGN REVENUE',
            renderValue: (value) => convertInputToFormattedAmountUSD(value),
          },
          {
            id: `employees`,
            label: 'EMPLOYEES',
            renderValue: (value) => (value ? value.toLocaleString() : ''),
          },
          {
            id: 'source',
            label: 'DATA SOURCE',
            render: (row) =>
              providerListWithoutPrimaryToDisplay(row.sources)?.join(', '),
          },
        ]}
      />
      <UnderwritingOverviewUnderwritingFinancialAnalysisSummary />
      <hr className="section-content-hr" />
      <NewFormTextField
        {...getFieldProps(
          'insuredEntity.primaryContact.firstName',
          isReadonly,

          'CONTACT FIRST NAME'
        )}
        placeholder="Enter Contact First Name"
      />
      <NewFormTextField
        {...getFieldProps(
          'insuredEntity.primaryContact.lastName',
          isReadonly,

          'CONTACT LAST NAME'
        )}
        placeholder="Enter Contact Last Name"
      />
      <NewFormTextField
        {...getFieldProps(
          'insuredEntity.primaryContact.email',
          isReadonly,

          'CONTACT EMAIL ADDRESS'
        )}
        placeholder="Enter Contact Email Address"
      />
      <NewFormTextField
        {...getFieldProps(
          'insuredEntity.primaryContact.phone',
          isReadonly,

          'CONTACT PHONE'
        )}
        placeholder="Enter Contact Phone Number"
      />
    </FormContainerWithHeader>
  );
};

const EstimatedRatableRevenueAlert = () => {
  const {
    submission: { insuredEntity },
  } = useUnderwritingSubmissionStateContext();

  const primaryFundamentals = insuredEntity?.node.fundamentals.edges.find(
    (fundamental) => fundamental.node.primary
  )?.node;

  const ratedRevenue = primaryFundamentals?.ratedRevenue;
  const formattedAmount = ratedRevenue
    ? convertInputToFormattedAmountUSD(ratedRevenue)
    : '$0';

  return (
    <Alert
      severity="info"
      variant="filled"
      sx={{
        backgroundColor: '#E5F6FD',
        color: '#0288D1',
        border: 'none',
        padding: '2px 10px',
        fontWeight: 600,
        '& .MuiAlert-icon': {
          color: '#0288D1',
        },
        '& .MuiAlert-message': {
          width: 'auto !important',
        },
      }}
    >
      Estimated Ratable Revenue:{' '}
      <span style={{ color: 'black', fontWeight: 500 }}>{formattedAmount}</span>
    </Alert>
  );
};
